import { useState } from 'react';

export interface PhotoCandidate {
  photoIndex?: number;
  id?: string;
  section?: string;
  score?: number;
  reason?: string;
  thumbUrl?: string;
  photographer?: string;
  url?: string;
}

export interface PhotoAnalysis {
  searchQueries?: string[];
  candidates?: PhotoCandidate[];
  selected?: Record<string, PhotoCandidate>;
  rejected?: Record<string, { reason?: string; idealDescription?: string; size?: string }>;
  generated?: Array<{ section?: string; prompt?: string; camera?: string; size?: string; generatedAt?: string; ninaReason?: string }>;
  finalDecision?: string;
  analyzedAt?: string;
}

export interface BrandingData {
  businessName?: string;
  activity?: string;
  artDirection?: {
    palette?: Record<string, string>;
    typography?: { headingFont?: string; bodyFont?: string; style?: string };
    tone?: string;
    mood?: string;
  };
  content?: {
    hero?: { title?: string; subtitle?: string };
    services?: { items?: Array<{ icon?: string; title?: string; description?: string }> };
    testimonials?: { items?: Array<{ name?: string; text?: string }> };
    about?: { sectionTitle?: string; paragraphs?: string[] };
    footer?: { tagline?: string };
    seo?: { title?: string; metaDescription?: string };
  };
  photos?: {
    hero?: string;
    about?: string;
    gallery?: string[];
  };
  photoAnalysis?: PhotoAnalysis;
  logoBrief?: {
    direction?: string;
    style?: string;
  };
}

export function parseBrandingData(raw: string | object | null | undefined): BrandingData | null {
  if (!raw) return null;
  try {
    if (typeof raw === 'string') return JSON.parse(raw);
    return raw as BrandingData;
  } catch { return null; }
}

function ColorSwatch({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div 
        className="w-6 h-6 rounded-md border border-gray-200 shadow-sm" 
        style={{ backgroundColor: color }}
      />
      <div className="text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="text-gray-700 ml-1 font-mono">{color}</span>
      </div>
    </div>
  );
}

export default function BrandingPanel({ data }: { data: BrandingData }) {
  const palette = data.artDirection?.palette || {};
  const services = data.content?.services?.items || [];
  const hero = data.content?.hero;
  const seo = data.content?.seo;

  return (
    <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
      <h4 className="text-sm font-bold text-purple-800 flex items-center gap-2 mb-3">
        🎨 Analyse Branding (Nina — CMO)
      </h4>

      {/* Tone & Mood */}
      {data.artDirection?.tone && (
        <div className="mb-3">
          <span className="text-xs text-gray-500">Ton :</span>
          <span className="ml-2 text-sm font-medium text-purple-700 capitalize">{data.artDirection.tone}</span>
          {data.artDirection?.mood && (
            <p className="text-xs text-gray-500 mt-0.5 italic">{data.artDirection.mood}</p>
          )}
        </div>
      )}

      {/* Hero tagline */}
      {hero?.title && (
        <div className="mb-3 p-2 bg-white/60 rounded-lg">
          <span className="text-xs text-gray-500">Hero :</span>
          <p className="text-sm font-semibold text-gray-800">{hero.title}</p>
          {hero.subtitle && <p className="text-xs text-gray-600">{hero.subtitle}</p>}
        </div>
      )}

      {/* Color Palette */}
      {Object.keys(palette).length > 0 && (
        <div className="mb-3">
          <span className="text-xs text-gray-500 block mb-1.5">Palette :</span>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(palette).map(([key, val]) => (
              <ColorSwatch key={key} color={val} label={key} />
            ))}
          </div>
        </div>
      )}

      {/* Typography */}
      {data.artDirection?.typography && (
        <div className="mb-3">
          <span className="text-xs text-gray-500">Typo :</span>
          <span className="ml-2 text-xs text-gray-700">
            {data.artDirection.typography.headingFont} / {data.artDirection.typography.bodyFont}
          </span>
        </div>
      )}

      {/* Services */}
      {services.length > 0 && (
        <div className="mb-3">
          <span className="text-xs text-gray-500 block mb-1">Services ({services.length}) :</span>
          <div className="flex flex-wrap gap-1.5">
            {services.map((s, i) => (
              <span key={i} className="px-2 py-0.5 bg-white/70 rounded-md text-xs text-gray-700">
                {s.icon} {s.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* SEO */}
      {seo?.title && (
        <div className="mb-3 p-2 bg-white/60 rounded-lg">
          <span className="text-xs text-gray-500">SEO Title :</span>
          <p className="text-xs text-gray-700">{seo.title}</p>
          {seo.metaDescription && (
            <p className="text-xs text-gray-500 mt-0.5">{seo.metaDescription}</p>
          )}
        </div>
      )}

      {/* Logo brief */}
      {data.logoBrief?.direction && (
        <div className="mb-1">
          <span className="text-xs text-gray-500">Logo brief :</span>
          <p className="text-xs text-gray-600 italic">{data.logoBrief.direction}</p>
        </div>
      )}

      {/* Photo Analysis (Nina) */}
      {data.photoAnalysis && <PhotoAnalysisPanel analysis={data.photoAnalysis} />}
    </div>
  );
}

function PhotoAnalysisPanel({ analysis }: { analysis: PhotoAnalysis }) {
  const [expanded, setExpanded] = useState(false);
  
  const selectedCount = Object.keys(analysis.selected || {}).length;
  const generatedCount = (analysis.generated || []).length;
  const queryCount = (analysis.searchQueries || []).length;
  
  return (
    <div className="mt-3 p-3 bg-white/60 rounded-lg border border-blue-100">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h5 className="text-xs font-bold text-blue-800 flex items-center gap-1.5">
          📸 Analyse Photos (Nina)
        </h5>
        <div className="flex items-center gap-2">
          <span className="text-xs text-blue-600">
            {selectedCount} Unsplash · {generatedCount} IA · {queryCount} recherches
          </span>
          <span className="text-xs text-gray-400">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>
      
      {expanded && (
        <div className="mt-2 space-y-2">
          {/* Search queries */}
          {analysis.searchQueries && analysis.searchQueries.length > 0 && (
            <div>
              <span className="text-xs text-gray-500 block mb-1">🔍 Recherches Unsplash :</span>
              <div className="flex flex-wrap gap-1">
                {analysis.searchQueries.map((q, i) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-50 rounded text-xs text-blue-700">"{q}"</span>
                ))}
              </div>
            </div>
          )}
          
          {/* Selected photos */}
          {analysis.selected && Object.keys(analysis.selected).length > 0 && (
            <div>
              <span className="text-xs text-gray-500 block mb-1">✅ Photos sélectionnées Unsplash :</span>
              <div className="space-y-1">
                {Object.entries(analysis.selected).map(([section, sel]) => (
                  <div key={section} className="flex items-center gap-2 text-xs">
                    {sel.thumbUrl && (
                      <img src={sel.thumbUrl} alt="" className="w-8 h-8 rounded object-cover" />
                    )}
                    <span className="font-medium text-gray-700">{section}</span>
                    <span className="text-green-600">score {sel.score}/10</span>
                    <span className="text-gray-400 truncate max-w-[200px]">{sel.reason}</span>
                    {sel.photographer && (
                      <span className="text-gray-300">by {sel.photographer}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Generated images */}
          {analysis.generated && analysis.generated.length > 0 && (
            <div>
              <span className="text-xs text-gray-500 block mb-1">🖼️ Images générées par IA :</span>
              <div className="space-y-1">
                {analysis.generated.map((gen, i) => (
                  <div key={i} className="text-xs p-1.5 bg-amber-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-700">{gen.section}</span>
                      <span className="text-amber-600">{gen.size}</span>
                      <span className="text-gray-400">{gen.camera}</span>
                    </div>
                    <p className="text-gray-500 mt-0.5 italic truncate">{gen.prompt}</p>
                    {gen.ninaReason && (
                      <p className="text-gray-400 mt-0.5">Nina: {gen.ninaReason}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Rejected sections (needed generation) */}
          {analysis.rejected && Object.keys(analysis.rejected).length > 0 && (
            <div>
              <span className="text-xs text-gray-500 block mb-1">❌ Rejetées (pas assez pertinentes) :</span>
              <div className="space-y-1">
                {Object.entries(analysis.rejected).map(([section, rej]) => (
                  <div key={section} className="text-xs">
                    <span className="font-medium text-gray-700">{section}</span>
                    <span className="text-red-500 ml-2">{rej.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Final decision */}
          {analysis.finalDecision && (
            <div className="pt-1.5 border-t border-blue-100">
              <span className="text-xs text-gray-500">💬 Décision Nina :</span>
              <p className="text-xs text-gray-700 mt-0.5 italic">{analysis.finalDecision}</p>
            </div>
          )}
          
          {analysis.analyzedAt && (
            <p className="text-xs text-gray-300 text-right">Analysé le {new Date(analysis.analyzedAt).toLocaleString('fr-FR')}</p>
          )}
        </div>
      )}
    </div>
  );
}
